import { createContext, FC, PropsWithChildren, useEffect, useMemo } from 'react';

import * as Sentry from '@sentry/browser';
import { parse } from 'querystringify';

import {
  createPayorderContextAction,
  getOrderDetailsAction,
  postPaymentContextAction,
} from './context/contextActions';
import { ThunkDispatcher, useThunkReducer } from './custom-hooks/thunkReducer';
import { LoadingState } from './enums/LoadingState';
import { PayorderBackendErrorMessage } from './enums/PayorderBackendErrorMessage';
import { SentryTag } from './enums/SentryTag';
import IOrderDetails from './interfaces/OrderDetails';
import PayorderBackendErrorResponse from './interfaces/PayorderBackendErrorResponse';
import PayorderLinkQueryParams from './interfaces/PayorderLinkQueryParams';

export const PAYMENT_CONTEXT_ID_LS_KEY = 'payment-context-id';
export const PAYORDER_CONTEXT_ID_LS_KEY = 'payorder-context-id';

export type Action =
  | { type: 'CreatePayorderContext' }
  | { type: 'CreatePayorderContextSuccess'; payorderContextId: string }
  | { type: 'CreatePayorderContextFailure'; error: PayorderBackendErrorResponse }
  | { type: 'RemoveContextIds' }
  | { type: 'CreatePaymentContext' }
  | { type: 'CreatePaymentContextSuccess'; paymentContextId: string }
  | { type: 'CreatePaymentContextFailure'; error: PayorderBackendErrorResponse }
  | { type: 'RemovePaymentContextId' }
  | { type: 'GetPaymentStatus'; refresh?: boolean }
  | { type: 'GetPaymentStatusFailure' }
  | { type: 'GetOrderDetails' }
  | { type: 'GetOrderDetailsSuccess'; orderDetails: IOrderDetails }
  | { type: 'GetOrderDetailsFailure' }
  | { type: 'SetQueryParams'; queryParams: PayorderLinkQueryParams }
  | { type: 'SetPayorderContextId'; payorderContextId: string }
  | { type: 'SetPaymentContextId'; paymentContextId: string }
  | { type: 'SetAmountLeftToPay'; amountLeftToPay: number };

interface ApiResponse<T> {
  response: T | null;
  loadingState: LoadingState;
}

export interface State {
  orderDetails: ApiResponse<IOrderDetails>;
  queryParams: PayorderLinkQueryParams;
  payorderContext: ContextState;
  paymentContext: ContextState;
  amountLeftToPay: number | null;
}

interface ContextProps {
  contextState: State;
  dispatchContext: ThunkDispatcher<State, Action>;
}

interface ContextState {
  loadingState: LoadingState;
  contextId: string | null;
  error?: any;
}

const contextSuccess = (contextId: string): ContextState => ({
  loadingState: LoadingState.Success,
  contextId,
});

const contextPending = (): ContextState => ({
  loadingState: LoadingState.Pending,
  contextId: null,
  error: null,
});

const contextEmpty = (): ContextState => ({
  loadingState: LoadingState.Empty,
  contextId: null,
  error: null,
});

const contextFailure = (error?: PayorderBackendErrorResponse): ContextState => ({
  loadingState: LoadingState.Failure,
  contextId: null,
  error,
});

const success = <T,>(response: T): ApiResponse<T> => ({
  response,
  loadingState: LoadingState.Success,
});

const failure = <T,>(): ApiResponse<T> => ({
  response: null,
  loadingState: LoadingState.Failure,
});

const empty = <T,>(): ApiResponse<T> => ({
  response: null,
  loadingState: LoadingState.Empty,
});

const pending = <T,>(response?: T | null): ApiResponse<T> => ({
  response: response || null,
  loadingState: LoadingState.Pending,
});

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'CreatePayorderContext':
      return { ...state, payorderContext: contextPending() };

    case 'CreatePayorderContextSuccess':
    case 'SetPayorderContextId': {
      const { payorderContextId } = action;
      Sentry.setExtra('payorderContextId', payorderContextId);
      localStorage.setItem(PAYORDER_CONTEXT_ID_LS_KEY, payorderContextId);
      return { ...state, payorderContext: contextSuccess(payorderContextId) };
    }
    case 'CreatePayorderContextFailure':
      return { ...state, payorderContext: contextFailure(action.error) };

    case 'RemoveContextIds':
      localStorage.removeItem(PAYORDER_CONTEXT_ID_LS_KEY);
      localStorage.removeItem(PAYMENT_CONTEXT_ID_LS_KEY);
      return { ...state, payorderContext: contextEmpty(), paymentContext: contextEmpty() };

    case 'RemovePaymentContextId':
      localStorage.removeItem(PAYMENT_CONTEXT_ID_LS_KEY);
      return { ...state, paymentContext: contextEmpty() };

    case 'CreatePaymentContext':
      return { ...state, paymentContext: contextPending() };

    case 'CreatePaymentContextSuccess':
    case 'SetPaymentContextId': {
      const { paymentContextId } = action;
      Sentry.setExtra('paymentContextId', paymentContextId);
      localStorage.setItem(PAYMENT_CONTEXT_ID_LS_KEY, paymentContextId);
      return { ...state, paymentContext: contextSuccess(paymentContextId) };
    }
    case 'CreatePaymentContextFailure':
      return {
        ...state,
        paymentContext: contextFailure(action.error),
      };

    case 'GetOrderDetails':
      return { ...state, orderDetails: pending() };

    case 'GetOrderDetailsSuccess':
      return {
        ...state,
        orderDetails: success(action.orderDetails),
      };

    case 'GetOrderDetailsFailure':
      return { ...state, orderDetails: failure() };

    case 'SetQueryParams':
      if (action.queryParams.variant) {
        Sentry.setTag(SentryTag.Variant, action.queryParams.variant);
      }
      return { ...state, queryParams: action.queryParams };

    case 'SetAmountLeftToPay':
      return { ...state, amountLeftToPay: action.amountLeftToPay };

    default:
      return state;
  }
};

const initialState: State = {
  orderDetails: empty(),
  queryParams: {},
  paymentContext: contextEmpty(),
  payorderContext: contextEmpty(),
  amountLeftToPay: null,
};

const PayorderContext = createContext<ContextProps>({
  contextState: initialState,
  dispatchContext: () => undefined,
});

const PayorderContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [contextState, dispatchContext] = useThunkReducer<State, Action>(reducer, initialState);
  const {
    payorderContext: { contextId: payorderContextId },
    paymentContext: { contextId: paymentContextId },
  } = contextState;

  useEffect(() => {
    const queryParams = parse(window.location.search) as PayorderLinkQueryParams;
    dispatchContext({ type: 'SetQueryParams', queryParams });

    const { validationToken, orderNumber } = queryParams;

    const payorderContextId = localStorage.getItem(PAYORDER_CONTEXT_ID_LS_KEY);
    const paymentContextId = localStorage.getItem(PAYMENT_CONTEXT_ID_LS_KEY);

    if (validationToken && orderNumber) {
      dispatchContext(createPayorderContextAction(validationToken, orderNumber));
    } else if (payorderContextId) {
      dispatchContext({ type: 'SetPayorderContextId', payorderContextId });

      if (paymentContextId) {
        dispatchContext({ type: 'SetPaymentContextId', paymentContextId });
      }
    } else {
      dispatchContext({
        type: 'CreatePayorderContextFailure',
        error: { status: 404, errorMessage: PayorderBackendErrorMessage.NotFound },
      });
    }
  }, [dispatchContext]);

  useEffect(() => {
    if (payorderContextId) {
      dispatchContext(getOrderDetailsAction(payorderContextId));
    }
  }, [dispatchContext, payorderContextId]);

  useEffect(() => {
    if (!paymentContextId && payorderContextId) {
      dispatchContext(postPaymentContextAction(payorderContextId));
    }
  }, [dispatchContext, paymentContextId, payorderContextId]);

  const contextValue = useMemo(() => {
    return { contextState, dispatchContext };
  }, [contextState, dispatchContext]);

  return <PayorderContext.Provider value={contextValue}>{children}</PayorderContext.Provider>;
};

export { PayorderContext, PayorderContextProvider };
